// Settings: workspace profile, products, team members, billing, API keys, and pipeline health.
//
// The slug is intentionally read-only: it's in URLs, the CLI, MCP tool args,
// and audit subject_refs. Renaming it would orphan every external reference.
// Currency and domain are safe to change; name obviously is.
import { useState, type FormEvent } from "react";
import { api, ApiError, type ProductRow } from "../api";
import { getSession, updateSession } from "../session";
import { useAsync } from "../useAsync";
import { go, type SettingsTab } from "../router";
import { shortTime, DASH } from "../format";
import { Team } from "./Team";
import { Billing } from "./Billing";
import { Pipeline } from "./Pipeline";

const CURRENCIES = ["USD", "AUD", "EUR", "GBP", "NZD", "CAD"];

type ApiKey = { id: string; name: string; prefix: string; created_at: string; last_used_at: string | null };

const TABS: Array<{ id: SettingsTab; label: string }> = [
  { id: "general", label: "General" },
  { id: "team", label: "Team" },
  { id: "billing", label: "Billing" },
  { id: "pipeline", label: "Pipeline" },
];

export function Settings({ tab = "general" }: { tab?: SettingsTab }) {
  const currentTab = tab ?? "general";

  return (
    <div className="page" style={{ paddingTop: 34 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>Settings</h1>
        <span className="sub" style={{ maxWidth: 660 }}>
          {currentTab === "team"
            ? "Everyone here shares this account's projects and action queue. Approvals and edits are recorded under each person's email."
            : currentTab === "billing"
              ? "Your plan, project subscriptions, limits and Stripe billing."
              : currentTab === "pipeline"
                ? "Whether the pipeline ran, when each source last delivered, and how much revenue the warehouse cannot attribute."
                : "Profile, workspace projects, and API keys. Integrations are wired on Integrations."}
        </span>
      </div>

      <nav style={{ display: "flex", gap: 6, marginTop: 22, marginBottom: 8 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={currentTab === t.id ? "btn-accent" : "btn-ghost"}
            aria-current={currentTab === t.id}
            onClick={() => go({ name: "settings", tab: t.id })}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {currentTab === "team" ? (
        <Team />
      ) : currentTab === "billing" ? (
        <Billing />
      ) : currentTab === "pipeline" ? (
        <Pipeline />
      ) : (
        <GeneralSettings />
      )}
    </div>
  );
}

function GeneralSettings() {
  const portfolio = useAsync(() => api.portfolio(), []);
  const keys = useAsync(() => api.auth.keys(), []);
  const session = getSession();

  if (portfolio.error) return <Problem message={portfolio.error} />;
  if (portfolio.loading || !portfolio.data) return <Loading />;

  return (
    <div style={{ maxWidth: 760 }}>
      {session && <ProfileSection email={session.account.email} initialName={session.account.name ?? ""} />}

      <section style={{ marginTop: 30 }}>
        <h2 style={{ marginBottom: 12 }}>Projects</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <NewProductCard />
          {portfolio.data.map((p) => (
            <ProductCard key={p.product_id} p={p} />
          ))}
        </div>
      </section>

      <ApiKeysSection keys={keys} />
    </div>
  );
}

// --------------------------------------------------------------- profile

function ProfileSection({ email, initialName }: { email: string; initialName: string }) {
  const [name, setName] = useState(initialName);
  const [nameSaved, setNameSaved] = useState<string | null>(null);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  const nameDirty = name !== initialName;

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    setNameSaved(null);
    try {
      const updated = await api.auth.updateMe({ name: name.trim() || null });
      updateSession({ name: updated.name ?? null });
      setName(updated.name ?? "");
      setNameSaved("Saved.");
    } catch (err) {
      setNameSaved(err instanceof ApiError ? err.message : String(err));
    }
  };

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setPwError("The new passwords don't match.");
      return;
    }
    setPwBusy(true);
    setPwError(null);
    setPwMsg(null);
    try {
      await api.auth.updateMe({ current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw(""); setConfirmPw("");
      setPwMsg("Changed. All other sessions were signed out — sign in again with the new password.");
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <section className="panel" style={{ marginTop: 26 }}>
      <h2>Your profile</h2>

      <form onSubmit={saveName} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14, maxWidth: 420 }}>
        <div className="field">
          <label htmlFor="s-name">Display name</label>
          <input id="s-name" className="input" value={name} maxLength={80}
                 onChange={(e) => { setName(e.target.value); setNameSaved(null); }} autoComplete="name" />
        </div>
        <div className="field">
          <label>Email</label>
          <input className="input" value={email} disabled title="Email changes aren't supported yet." />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button type="submit" className="btn-solid" disabled={!nameDirty}>Save name</button>
          {nameSaved && (
            <span style={{ fontSize: 12.5, color: nameSaved === "Saved." ? "var(--pos)" : "var(--neg)" }}>
              {nameSaved}
            </span>
          )}
        </div>
      </form>

      <form onSubmit={changePassword}
            style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24, maxWidth: 420,
                     paddingTop: 18, borderTop: "1px solid var(--border-hairline)" }}>
        <h3 style={{ fontSize: 13.5, marginBottom: 0 }}>Change password</h3>
        <div className="field">
          <label htmlFor="s-cur">Current password</label>
          <input id="s-cur" className="input" type="password" required value={curPw}
                 onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" />
        </div>
        <div className="field">
          <label htmlFor="s-new">New password</label>
          <input id="s-new" className="input" type="password" required minLength={10} value={newPw}
                 onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
        </div>
        <div className="field">
          <label htmlFor="s-conf">Confirm new password</label>
          <input id="s-conf" className="input" type="password" required minLength={10} value={confirmPw}
                 onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password" />
        </div>
        {pwError && <div className="form-error">{pwError}</div>}
        {pwMsg && <div style={{ fontSize: 12.5, color: "var(--pos)" }}>{pwMsg}</div>}
        <div>
          <button type="submit" className="btn-solid" disabled={pwBusy || !curPw || !newPw || !confirmPw}>
            {pwBusy ? "Changing…" : "Change password"}
          </button>
        </div>
        <div className="form-note">
          Changing your password signs out every other browser and CLI. This one keeps working until you reload.
        </div>
      </form>
    </section>
  );
}

// --------------------------------------------------------------- products

// Creating lives in one place — the onboarding wizard — so every new product
// walks the same create → connect → track path instead of landing empty.
function NewProductCard() {
  return (
    <button type="button" className="btn-ghost" style={{ alignSelf: "flex-start" }}
            onClick={() => go({ name: "newProduct" })}>
      New project
    </button>
  );
}

function ProductCard({ p }: { p: ProductRow }) {
  const [name, setName] = useState(p.name);
  const [currency, setCurrency] = useState(p.currency);
  const [domain, setDomain] = useState<string>(p.domain ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dirty = name !== p.name || currency !== p.currency || domain !== (p.domain ?? "");

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    setError(null);
    try {
      const updated = await api.updateProduct(p.slug, {
        name: name !== p.name ? name : undefined,
        currency: currency !== p.currency ? currency : undefined,
        domain: domain !== (p.domain ?? "") ? (domain.trim() || null) : undefined,
      });
      setName(updated.name);
      setCurrency(updated.currency);
      setDomain(updated.domain ?? "");
      setMsg("Saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setName(p.name);
    setCurrency(p.currency);
    setDomain(p.domain ?? "");
    setMsg(null);
    setError(null);
  };

  return (
    <div className="panel" style={{ padding: "18px 20px 16px" }}>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 14 }}>
          <div className="field">
            <label htmlFor={`p-name-${p.slug}`}>Project name</label>
            <input id={`p-name-${p.slug}`} className="input" required value={name} maxLength={120}
                   onChange={(e) => { setName(e.target.value); setMsg(null); }} />
          </div>
          <div className="field">
            <label htmlFor={`p-cur-${p.slug}`}>Currency</label>
            <select id={`p-cur-${p.slug}`} className="input" value={currency}
                    onChange={(e) => { setCurrency(e.target.value); setMsg(null); }}>
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor={`p-domain-${p.slug}`}>Domain</label>
          <input id={`p-domain-${p.slug}`} className="input" value={domain} maxLength={255}
                 placeholder="example.com"
                 onChange={(e) => { setDomain(e.target.value); setMsg(null); }} />
        </div>
        <div className="field">
          <label>Slug</label>
          <input className="input num" value={p.slug} disabled
                 title="The slug is in URLs, the CLI, and audit refs — it can't be renamed." />
        </div>
        {error && <div className="form-error">{error}</div>}
        {msg && <span style={{ fontSize: 12.5, color: "var(--pos)" }}>{msg}</span>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" className="btn-solid" disabled={busy || !dirty}>Save changes</button>
          <button type="button" className="btn-ghost" disabled={!dirty || busy} onClick={reset}>Reset</button>
        </div>
      </form>
    </div>
  );
}

// --------------------------------------------------------------- api keys

function ApiKeysSection({ keys }: { keys: ReturnType<typeof useAsync<ApiKey[]>> }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await api.auth.createKey(name.trim() || "default");
      setCreated({ key: r.key, name: r.name });
      setName("");
      keys.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy — select the key and copy it yourself.");
    }
  };

  const revoke = async (id: string) => {
    setRevokeId(id);
    setRevokeError(null);
    try {
      await api.auth.revokeKey(id);
      keys.reload();
    } catch (err) {
      setRevokeError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRevokeId(null);
    }
  };

  return (
    <section style={{ marginTop: 30 }}>
      <h2 style={{ marginBottom: 12 }}>API keys</h2>

      <div className="panel" style={{ marginBottom: 14 }}>
        <form onSubmit={create} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="k-name">New key name</label>
            <input id="k-name" className="input" value={name} maxLength={80}
                   placeholder="e.g. production, ci, claude"
                   onChange={(e) => setName(e.target.value)} />
          </div>
          <button type="submit" className="btn-accent" style={{ minHeight: 42 }} disabled={busy}>
            {busy ? "Minting…" : "Create key"}
          </button>
        </form>
        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
        {created && (
          <div className="banner" style={{ marginTop: 14, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
            <span>
              Key <strong>{created.name}</strong> created — shown once. Store it now; you won't see it again.
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ flex: 1, minWidth: 0, fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis",
                             whiteSpace: "nowrap", background: "var(--surface)", border: "1px solid var(--border)",
                             borderRadius: 6, padding: "8px 10px" }}>
                {created.key}
              </code>
              <button type="button" className="btn-ghost" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
              <button type="button" className="btn-link" onClick={() => setCreated(null)}>Dismiss</button>
            </div>
          </div>
        )}
        <div className="form-note" style={{ marginTop: 10 }}>
          Keys authenticate the <code>fk</code> CLI, MCP, and API calls. Revoking a key takes effect immediately.
        </div>
      </div>

      {keys.error ? (
        <div className="form-error">{keys.error}</div>
      ) : keys.loading || !keys.data ? (
        <div style={{ color: "var(--text-faint)", fontSize: 13 }}>Loading…</div>
      ) : keys.data.length === 0 ? (
        <div style={{ color: "var(--text-faint)", fontSize: 13 }}>No active keys.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="ptable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Created</th>
                <th>Last used</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.data.map((k) => (
                <tr key={k.id}>
                  <td><div className="cell">{k.name}</div></td>
                  <td><div className="cell num">{k.prefix}</div></td>
                  <td><div className="cell num">{shortTime(k.created_at)}</div></td>
                  <td><div className="cell num">{k.last_used_at ? shortTime(k.last_used_at) : DASH}</div></td>
                  <td>
                    <div className="cell" style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button type="button" className="btn-link" style={{ color: "var(--neg)" }}
                              disabled={revokeId === k.id}
                              onClick={() => revoke(k.id)}>
                        {revokeId === k.id ? "Revoking…" : "Revoke"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {revokeError && <div className="form-error" style={{ marginTop: 8 }}>{revokeError}</div>}
        </div>
      )}
    </section>
  );
}

// --------------------------------------------------------------- shared

function Loading() {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--text-faint)" }}>Loading…</div>;
}

function Problem({ message }: { message: string }) {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--neg)" }}>{message}</div>;
}
